import BaseResource from '@/lib/data/resource/BaseResource'
import { toNumberFormatNotationShort } from '@/lib/utils/helper'

export default class ALMSummaryResource extends BaseResource {
  constructor (payload = {}) {
    super(payload)

    this.Summary = payload.summary ?? {}
    this.AssetsLiabilityMix = this.Summary.assetsLiabilityMix ?? {}
    this.Liquidity = this.Summary.liquidity ?? {}
    this.Profitability = this.Summary.profitability ?? {}
    this.GapAnalysis = this.Summary.gapAnalysis ?? {}
    this.baseCurrency = payload.baseCurrency ?? {}
    this.selectedPeriod = payload.selectedPeriod || '12m'
    this.selectedRateType = payload.selectedRateType || 'floating'
    this.selectedGapFilter = payload.selectedGapFilter || 'Total'
  }

  /* ----------------- Helpers ----------------- */
  _num (n) { return Number.isFinite(n) ? n : 0 }
  _pct (part, total) { return total > 0 ? Math.round((part / total) * 100) : 0 }
  _pair (value, total) {
    const v = Math.max(0, this._num(value))
    const t = Math.max(0, this._num(total))
    const remainder = Math.max(0, t - v)
    if (t === 0) return []
    return [
      { name: 'value', value: v, type: 'number' },
      { name: 'remainder', value: remainder, type: 'number' }
    ]
  }

  _fmt (v) {
    const n = this._num(v || 0)
    const isNeg = n < 0
    return (isNeg ? '-' : '') + this.currencySymbol + toNumberFormatNotationShort(Math.abs(n))
  }

  get currencySymbol () {
    return this.baseCurrency?.displaySymbol || '$'
  }

  get _totalAssets () {
    const { loans = 0, investments = 0 } = this.AssetsLiabilityMix
    return this._num(loans) + this._num(investments)
  }

  get _totalLiabilities () {
    const { deposits = 0, equity = 0 } = this.AssetsLiabilityMix
    return this._num(deposits) + this._num(equity)
  }

  /* -------- Single-metric pies for the 4 cards -------- */
  get LoansPieData () {
    return this._pair(this._num(this.AssetsLiabilityMix.loans), this._totalAssets)
  }

  get InvestmentsPieData () {
    return this._pair(this._num(this.AssetsLiabilityMix.investments), this._totalAssets)
  }

  get DepositsPieData () {
    return this._pair(this._num(this.AssetsLiabilityMix.deposits), this._totalLiabilities)
  }

  get EquityPieData () {
    return this._pair(this._num(this.AssetsLiabilityMix.equity), this._totalLiabilities)
  }

  /* ---------------- Percent labels ---------------- */
  get AssetsPercentages () {
    const loans = this._num(this.AssetsLiabilityMix.loans)
    const investments = this._num(this.AssetsLiabilityMix.investments)
    return {
      loans,
      investments
    }
  }

  get LiabilitiesPercentages () {
    const deposits = this._num(this.AssetsLiabilityMix.deposits)
    const equity = this._num(this.AssetsLiabilityMix.equity)
    return {
      deposits,
      equity
    }
  }

  /* ---------------- Liquidity Metrics ---------------- */
  get liquidityCoverage () {
    return this._num(this.Liquidity.liquidityCoverage)
  }

  get liquidityCoverageFmt () {
    return this.liquidityCoverage + '%'
  }

  get loansVsDepositsFactor () {
    return this._num(this.Liquidity.loansVsDepositsFactor)
  }

  get loansVsDepositsFactorFmt () {
    return this.loansVsDepositsFactor + 'x'
  }

  get liquidVsTotalAssets () {
    return this._num(this.Liquidity.liquidVsTotalAssets)
  }

  get liquidVsTotalAssetsFmt () {
    return this.liquidVsTotalAssets + '%'
  }

  get coreVsTotalDeposits () {
    return this._num(this.Liquidity.coreVsTotalDeposits)
  }

  get coreVsTotalDepositsFmt () {
    return this.coreVsTotalDeposits + '%'
  }

  get uninsuredVsTotalDeposits () {
    return this._num(this.Liquidity.uninsuredVsTotalDeposits)
  }

  get uninsuredVsTotalDepositsFmt () {
    return this.uninsuredVsTotalDeposits + '%'
  }

  /* ---------------- Profitability Metrics ---------------- */
  get profitabilityPieData () {
    const perActivityData = this.Profitability?.perActivityProfitability || []

    const profitField = this.selectedPeriod === '3m' ? 'profit3m' : 'profit12m'

    const pieData = []
    perActivityData.forEach(activity => {
      const value = this._num(activity[profitField] || 0)

      pieData.push({
        name      : activity.bankActivity,
        value     : Math.abs(value),
        isNegative: value < 0,
        type      : 'number'
      })
    })

    return pieData
  }

  get profitabilityBankActivities () {
    const perActivityData = this.Profitability?.perActivityProfitability || []
    const netInterestData = this.Profitability?.netInterestIncomeProfitability || {}

    const colorMap = {
      Loans      : '#57C05B',
      Deposits   : '#3D68D5',
      Investments: '#EA804B'
    }

    const cellColorMap = {
      Loans      : '#EDF7ED',
      Deposits   : '#FDF4F0',
      Investments: '#E7ECFA'
    }

    const activities = perActivityData.map(activity => {
      const threeMonths = this._fmt(activity.profit3m)
      const twelveMonths = this._fmt(activity.profit12m)

      return {
        name : activity.bankActivity,
        color: colorMap[activity.bankActivity],
        style: {
          'background-color': cellColorMap[activity.bankActivity]
        },
        threeMonths    : threeMonths,
        threeMonthsRaw : activity.profit3m,
        twelveMonths   : twelveMonths,
        twelveMonthsRaw: activity.profit12m,
        yield          : this._num(activity.yield || 0).toFixed(1) + '%'
      }
    })

    if (netInterestData.bankActivity) {
      const threeMonths = this._fmt(netInterestData.profit3m)
      const twelveMonths = this._fmt(netInterestData.profit12m)

      activities.push({
        name : netInterestData.bankActivity,
        color: '#9E9E9E',
        style: {
          'background-color': 'transparent',
          border            : '1px solid #DFE3EB',
          font              : 700
        },
        threeMonths    : threeMonths,
        threeMonthsRaw : netInterestData.profit3m,
        twelveMonths   : twelveMonths,
        twelveMonthsRaw: netInterestData.profit12m,
        yield          : this._num(netInterestData.yield || 0).toFixed(1) + '%'
      })
    }

    return activities
  }

  /* ---------------- Gap Analysis Metrics ---------------- */
  get gapAnalysisLabels () {
    return this.GapAnalysis?.labels
  }

  get gapAnalysisFixedRate () {
    return this.GapAnalysis?.fixedRate || []
  }

  get gapAnalysisFloatingRate () {
    return this.GapAnalysis?.floatingRate || []
  }

  get gapAnalysisChartData () {
    const rateType = this.selectedRateType || 'floating'
    const data = rateType === 'fixed' ? this.gapAnalysisFixedRate : this.gapAnalysisFloatingRate

    return data.map(item => ({
      label      : item.label,
      total      : this._num(item.total || 0),
      loans      : this._num(item.loans || 0),
      deposits   : this._num(item.deposits || 0),
      investments: this._num(item.investments || 0)
    }))
  }

  get gapAnalysisBarData () {
    const chartData = this.gapAnalysisChartData
    const selectedFilter = this.selectedGapFilter || 'Total'

    // For filters, return single bars
    return chartData.map(item => {
      let value = 0

      if (selectedFilter === 'Total') {
        value = item.total
      } else if (selectedFilter === 'Loans') {
        value = item.loans
      } else if (selectedFilter === 'Deposits') {
        value = item.deposits
      } else if (selectedFilter === 'Investments') {
        value = item.investments
      }

      return {
        label         : item.label,
        value         : value,
        formattedValue: this.currencySymbol + toNumberFormatNotationShort(Math.abs(value))
      }
    })
  }

  // New method for "All 3" view - returns stacked data
  get gapAnalysisStackedData () {
    const chartData = this.gapAnalysisChartData

    const d = chartData.map(item => ({
      label      : item.label,
      loans      : item.loans,
      deposits   : item.deposits,
      investments: item.investments,
      total      : item.total
    }))

    return d
  }
}
